import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Printer, CheckCircle2 } from 'lucide-react';
import { CLINIC, DOCTORS } from '../mock';
import { LETTER_TEMPLATES } from '../components/letterTemplates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REG = 'DMC Reg. No. 56020  ·  MCI Reg. No. 11/40599';
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { patient_name: '', age_sex: '', country: '', patient_ref: '', consult_date: today(), consult_type: 'Online video consultation', recipient: '', diagnosis: '', history: '', findings: '', investigations: '', plan: '', monitoring: '', red_flags: '', follow_up: '' };

const DETAILS = [
  ['patient_name', 'Patient name *'], ['age_sex', 'Age / sex'], ['country', 'Country of residence'],
  ['patient_ref', 'Clinic reference'], ['consult_date', 'Consultation date', 'date'],
];
const SECTIONS = [
  ['diagnosis', 'Diagnosis', 'e.g. Seropositive rheumatoid arthritis (anti-CCP positive), moderate disease activity'],
  ['history', 'Clinical summary', 'Presenting symptoms, duration, relevant history and previous treatment'],
  ['findings', 'Findings', 'Examination / findings on video consultation'],
  ['investigations', 'Investigations reviewed', 'Blood tests, imaging and dates'],
  ['plan', 'Recommended treatment plan', 'Medicines with dose, route, frequency and titration'],
  ['monitoring', 'Monitoring', 'e.g. FBC, LFT, creatinine every 4 weeks for 3 months, then every 3 months'],
  ['red_flags', 'Red flags / when to seek urgent care', 'e.g. fever on immunosuppression, new breathlessness, visual loss'],
  ['follow_up', 'Follow-up', 'e.g. Review in 6 weeks by video'],
];
const INPUT = 'w-full px-3 py-2.5 rounded-xl border border-teal-100 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition text-sm';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

function LetterPreview({ f }) {
  const doc = DOCTORS[0];
  return (
    <div id="letter-print" data-testid="letter-preview" className="bg-white text-[13px] leading-relaxed text-slate-800 p-10 shadow-xl rounded-sm" style={{ fontFamily: 'Georgia, serif' }}>
      <div className="flex justify-between gap-6 border-b-2 pb-4" style={{ borderColor: '#0f766e' }}>
        <div>
          <div className="text-xl font-bold" style={{ color: '#0b3b3a' }}>Srigyan Rheumatology Clinic</div>
          <div className="font-semibold mt-1">{doc.name}</div>
          <div className="text-xs text-slate-600">{doc.creds}</div>
          <div data-testid="letter-reg-numbers" className="text-xs text-slate-600 mt-0.5">{REG}</div>
        </div>
        <div className="text-right text-xs text-slate-600 max-w-[220px]">
          <div>{CLINIC.address}</div>
          <div className="mt-1">{CLINIC.phone}</div>
          <div>{CLINIC.email}</div>
        </div>
      </div>
      <div className="flex justify-between mt-5 text-xs">
        <div>{f.consult_type}</div>
        <div>Date: {fmtDate(f.consult_date)}</div>
      </div>
      <p className="mt-4 whitespace-pre-wrap">{f.recipient || 'To the treating doctor,'}</p>
      <p className="mt-4"><strong>Re: {f.patient_name || '[Patient name]'}</strong>{f.age_sex && `, ${f.age_sex}`}{f.country && `, resident in ${f.country}`}{f.patient_ref && ` (Ref: ${f.patient_ref})`}</p>
      <p className="mt-3">Thank you for continuing the care of this patient, whom I reviewed on {fmtDate(f.consult_date) || '[date]'}. Please find my assessment and recommendations below.</p>
      {SECTIONS.filter(([k]) => f[k]?.trim()).map(([k, label]) => (
        <section key={k} className="mt-4">
          <h4 className="font-bold text-[13px]" style={{ color: '#0b3b3a', fontFamily: 'Georgia, serif' }}>{label}</h4>
          <p className="whitespace-pre-wrap mt-1">{f[k]}</p>
        </section>
      ))}
      <p className="mt-5">Prescriptions should be issued by the local treating doctor in line with local regulations. Please do not hesitate to contact me if you need any further information.</p>
      <div className="mt-8">
        <p>Yours sincerely,</p>
        <div className="h-12" />
        <p className="font-semibold">{doc.name}</p>
        <p className="text-xs text-slate-600">{doc.role}, {doc.creds}</p>
        <p className="text-xs text-slate-600">{REG}</p>
      </div>
    </div>
  );
}

export default function LetterEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [status, setStatus] = useState('idle');
  const isNew = id === 'new';

  useEffect(() => {
    if (!isNew) axios.get(`${API}/letters/${id}`, { withCredentials: true }).then(({ data }) => setF({ ...EMPTY, ...data })).catch(() => setStatus('error'));
  }, [id, isNew]);

  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setStatus('idle'); };
  const applyTemplate = (t) => {
    const hasText = Object.keys(t.fields).some((k) => f[k]?.trim());
    if (hasText && !window.confirm(`Replace the clinical sections with the ${t.label} template?`)) return;
    setF({ ...f, ...t.fields });
    setStatus('idle');
  };
  const save = async () => {
    if (!f.patient_name.trim()) { setStatus('name'); return; }
    setStatus('saving');
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] || '']));
      const { data } = isNew ? await axios.post(`${API}/letters`, body, { withCredentials: true }) : await axios.put(`${API}/letters/${id}`, body, { withCredentials: true });
      setStatus('saved');
      if (isNew) navigate(`/dashboard/letters/${data.id}`, { replace: true });
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen py-10 print:bg-white print:py-0 print:min-h-0">
      <div className="container-x">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 print:hidden">
          <Link data-testid="letter-back" to="/dashboard?tab=letters" className="inline-flex items-center gap-2 text-sm text-teal-700 font-semibold"><ArrowLeft size={16} /> Back to letters</Link>
          <div className="flex items-center gap-3">
            {status === 'saved' && <span data-testid="letter-saved" className="inline-flex items-center gap-1 text-sm text-teal-700"><CheckCircle2 size={16} /> Saved</span>}
            {status === 'name' && <span data-testid="letter-name-error" className="text-sm" style={{ color: 'var(--coral-dark)' }}>Patient name is required</span>}
            {status === 'error' && <span data-testid="letter-error" className="text-sm" style={{ color: 'var(--coral-dark)' }}>Something went wrong</span>}
            <button data-testid="letter-save" onClick={save} disabled={status === 'saving'} className="btn-teal inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60"><Save size={16} /> {status === 'saving' ? 'Saving…' : 'Save'}</button>
            <button data-testid="letter-print" onClick={() => window.print()} className="btn-coral inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"><Printer size={16} /> Print / Save PDF</button>
          </div>
        </div>
        <div className="grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-8 items-start">
          <div data-testid="letter-form" className="bg-white rounded-2xl border border-teal-50 p-6 space-y-4 print:hidden">
            <h1 className="font-display text-2xl text-teal-900" style={{ fontWeight: 600 }}>{isNew ? 'New consult letter' : 'Edit consult letter'}</h1>
            <div data-testid="letter-templates" className="rounded-xl bg-teal-50/60 p-4">
              <div className="text-xs font-semibold text-teal-800 mb-2">Start from a template</div>
              <div className="flex flex-wrap gap-2">
                {LETTER_TEMPLATES.map((t) => (
                  <button key={t.id} type="button" data-testid={`letter-template-${t.id}`} onClick={() => applyTemplate(t)} className="px-3.5 py-2 rounded-full bg-white border border-teal-100 text-sm font-medium text-teal-800 hover:bg-teal-700 hover:text-white transition-colors">{t.label}</button>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Replace every [bracketed] item and check doses for each patient before printing.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {DETAILS.map(([k, label, type]) => (
                <label key={k} className="block"><span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
                  <input data-testid={`letter-${k}`} type={type || 'text'} value={f[k]} onChange={set(k)} className={INPUT} /></label>
              ))}
              <label className="block"><span className="block text-xs font-medium text-slate-600 mb-1">Consultation type</span>
                <select data-testid="letter-consult_type" value={f.consult_type} onChange={set('consult_type')} className={`${INPUT} bg-white`}>
                  <option>Online video consultation</option><option>In-clinic consultation</option>
                </select></label>
            </div>
            <label className="block"><span className="block text-xs font-medium text-slate-600 mb-1">Addressed to (local doctor name, practice, address)</span>
              <textarea data-testid="letter-recipient" rows={3} value={f.recipient} onChange={set('recipient')} className={`${INPUT} resize-y`} placeholder={'Dr ... (GP)\nPractice name, city, country'} /></label>
            {SECTIONS.map(([k, label, ph]) => (
              <label key={k} className="block"><span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
                <textarea data-testid={`letter-${k}`} rows={k === 'plan' ? 5 : 3} value={f[k]} onChange={set(k)} placeholder={ph} className={`${INPUT} resize-y`} /></label>
            ))}
          </div>
          <div className="xl:sticky xl:top-6 print:static"><LetterPreview f={f} /></div>
        </div>
      </div>
    </div>
  );
}
